import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    IoDocumentOutline,
    IoDocumentTextOutline,
    IoImageOutline,
} from "react-icons/io5";
import GetData, { fetchAgreementBlob } from "../../../services/getData";
import { Button, IconButton, SegmentedControl } from "../../../components/ui";
import "../../../components/ui/tabs.css";
import "../patients.css";
import "./patientDocs.css";

const AGREEMENT_SPECIALTIES = [
    { id: "therapy", labelKey: "agreements.specialties.therapy" },
    { id: "endocrinology", labelKey: "agreements.specialties.endocrinology" },
    { id: "gynecology", labelKey: "agreements.specialties.gynecology" },
    { id: "cosmetology", labelKey: "agreements.specialties.cosmetology" },
];

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

/** Плашка слева: иконка и подложка — как .pd-doc__ic в диагностике. */
const getFileIconClass = (file) => {
    const ext = getRealExtension(file);
    if (IMAGE_EXTENSIONS.includes(ext)) return "pd-doc__ic--img";
    if (ext === "pdf") return "pd-doc__ic--pdf";
    return "";
};

const renderFileIcon = (file) => {
    const ext = getRealExtension(file);
    if (IMAGE_EXTENSIONS.includes(ext)) return <IoImageOutline size={20} />;
    if (ext === "pdf") return <IoDocumentTextOutline size={20} />;
    return <IoDocumentOutline size={20} />;
};

/** ISO-дата → дд.мм.гггг; если пришла уже отформатированная — как есть. */
const formatDate = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const [datePart] = raw.split(/[ T]/);
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return datePart;
    return `${day}.${month}.${year}`;
};

const getFileExtension = (name) => {
    const parts = String(name || "").toLowerCase().split(".");
    return parts.length > 1 ? parts[parts.length - 1] : "";
};

/**
 * Расширение реального файла: сначала путь на диске, затем отображаемое имя,
 * и только потом расширение, пришедшее с бэкенда.
 * Имя в БД может не совпадать с фактическим файлом (например, .docx назван .pdf).
 */
const getRealExtension = (file) =>
    getFileExtension(file?.path) ||
    getFileExtension(file?.name) ||
    String(file?.ext || "").toLowerCase();

const normalizeAgreementItem = (item) => {
    if (!item || typeof item !== "object") return null;

    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!name) return null;

    const specialtiesRaw = Array.isArray(item.specialties)
        ? item.specialties
        : typeof item.specialty === "string"
          ? [item.specialty]
          : [];

    const specialties = specialtiesRaw
        .filter((value) => typeof value === "string" && value.trim())
        .map((value) => value.trim());

    return {
        id: Number(item.id) || 0,
        name,
        path: typeof item.path === "string" ? item.path.trim() : "",
        ext: typeof item.ext === "string" ? item.ext.trim().toLowerCase() : "",
        mime: typeof item.mime === "string" ? item.mime.trim().toLowerCase() : "",
        date: typeof item.date === "string" ? item.date.trim() : "",
        specialties,
    };
};

export default function PatientAgreements() {
    const { t } = useTranslation("patient");
    const [agreementFiles, setAgreementFiles] = useState([]);
    const [agreementSpecialty, setAgreementSpecialty] = useState("cosmetology");
    const [previewFile, setPreviewFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewMime, setPreviewMime] = useState("");
    const [previewError, setPreviewError] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);
    const previewFrameRef = useRef(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [printingId, setPrintingId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const loadAgreementFiles = async () => {
            try {
                const data = await GetData({
                    module: "patient",
                    block: "agreements",
                    action: "list",
                });
                const files = Array.isArray(data?.result) ? data.result : [];
                if (!cancelled) {
                    setAgreementFiles(files.map(normalizeAgreementItem).filter(Boolean));
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setAgreementFiles([]);
            }
        };
        loadAgreementFiles();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleOpenAgreementPreview = (file) => {
        const documentId = Number(file?.id);
        if (!documentId) {
            setAlertMessage(t("agreements.alert.noId"));
            return;
        }
        if (!file?.path) {
            setAlertMessage(t("agreements.alert.noPath"));
            return;
        }
        setPreviewFile(file);
        setPreviewUrl("");
        setPreviewMime("");
        setPreviewError("");
        setPreviewLoading(true);
    };

    const handleCloseAgreementPreview = () => {
        setPreviewFile(null);
        setPreviewUrl("");
        setPreviewMime("");
        setPreviewError("");
        setPreviewLoading(false);
    };

    useEffect(() => {
        if (!previewFile) return undefined;

        const documentId = Number(previewFile.id);
        if (!documentId) {
            setPreviewLoading(false);
            setPreviewError(t("agreements.alert.noId"));
            return undefined;
        }
        if (!previewFile.path) {
            setPreviewLoading(false);
            setPreviewError(t("agreements.alert.noPath"));
            return undefined;
        }

        let cancelled = false;
        let objectUrl = "";

        (async () => {
            setPreviewLoading(true);
            setPreviewError("");
            try {
                const blob = await fetchAgreementBlob(documentId, true);
                objectUrl = URL.createObjectURL(blob);
                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }
                setPreviewMime(String(blob.type || "").toLowerCase());
                setPreviewUrl(objectUrl);
            } catch (err) {
                if (!cancelled) {
                    setPreviewError(err?.message || t("agreements.alert.loadFailed"));
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [previewFile, t]);

    const previewExt = getRealExtension(previewFile);
    const declaredMime = String(previewFile?.mime || "").toLowerCase();
    const effectiveMime = previewMime || declaredMime;
    const hasGenericMime =
        !effectiveMime || effectiveMime === "application/octet-stream";

    // PDF считаем только если это подтверждает MIME самого файла либо
    // расширение файла на диске. Отображаемое имя из БД здесь не участвует.
    const isPdfPreview = hasGenericMime
        ? previewExt === "pdf"
        : effectiveMime.includes("pdf");
    const isImagePreview = hasGenericMime
        ? IMAGE_EXTENSIONS.includes(previewExt)
        : effectiveMime.startsWith("image/");
    const canPrint = Boolean(previewUrl) && (isPdfPreview || isImagePreview);

    const printFromUrl = (url, mime, fileName, extHint = "") => {
        const ext = getFileExtension(fileName) || extHint;
        const isPdf = String(mime || "").includes("pdf") || ext === "pdf";
        const isImage =
            String(mime || "").startsWith("image/") ||
            IMAGE_EXTENSIONS.includes(ext);

        if (!isPdf && !isImage) {
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName || "agreement";
            link.click();
            return false;
        }

        const printWindow = window.open(url, "_blank");
        if (!printWindow) {
            setAlertMessage(t("agreements.alert.popupBlocked"));
            return false;
        }
        printWindow.addEventListener("load", () => {
            printWindow.focus();
            printWindow.print();
        });
        return true;
    };

    const handlePrintAgreement = () => {
        if (!previewUrl) return;

        if (isPdfPreview && previewFrameRef.current?.contentWindow) {
            previewFrameRef.current.contentWindow.focus();
            previewFrameRef.current.contentWindow.print();
            return;
        }

        printFromUrl(previewUrl, effectiveMime, previewFile?.name, previewExt);
    };

    const handlePrintAgreementFile = async (file) => {
        const documentId = Number(file?.id);
        if (!documentId) {
            setAlertMessage(t("agreements.alert.noId"));
            return;
        }
        if (!file?.path) {
            setAlertMessage(t("agreements.alert.noPath"));
            return;
        }

        setPrintingId(documentId);
        let objectUrl = "";
        try {
            const blob = await fetchAgreementBlob(documentId, true);
            objectUrl = URL.createObjectURL(blob);
            const opened = printFromUrl(
                objectUrl,
                blob.type || file.mime,
                file.name,
                getRealExtension(file),
            );
            if (opened) {
                window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
            } else {
                URL.revokeObjectURL(objectUrl);
                const ext = getRealExtension(file);
                setAlertMessage(
                    t("agreements.alert.cantPrint", {
                        format: ext ? " " + ext.toUpperCase() : "",
                    }),
                );
            }
        } catch (err) {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            setAlertMessage(err?.message || t("agreements.alert.loadFailed"));
        } finally {
            setPrintingId(null);
        }
    };

    const filteredAgreementFiles = agreementFiles.filter((file) => {
        if (!Array.isArray(file.specialties) || !file.specialties.includes(agreementSpecialty)) {
            return false;
        }
        return true;
    });

    return (
        <>
            <div className="patient-agreements-block pd">
                {/* Внутренние табы специальностей — общий SegmentedControl */}
                <div className="patient-agreements-tabs">
                    <SegmentedControl
                        ariaLabel={t("agreements.docTypes")}
                        items={AGREEMENT_SPECIALTIES.map((specialty) => ({
                            value: specialty.id,
                            label: t(specialty.labelKey),
                        }))}
                        onChange={setAgreementSpecialty}
                        value={agreementSpecialty}
                    />
                </div>

                {agreementFiles.length === 0 ? (
                    <div className="pd-empty">{t("agreements.filesNotFound")}</div>
                ) : filteredAgreementFiles.length === 0 ? (
                    <div className="pd-empty">{t("agreements.nothingFound")}</div>
                ) : (
                    <div className="pd-list">
                        {filteredAgreementFiles.map((file) => {
                            const fileDate = formatDate(file.date);
                            const isPrinting = printingId === file.id;

                            return (
                                <div className="pd-doc" key={file.id || file.name}>
                                    <div
                                        aria-hidden="true"
                                        className={`pd-doc__ic ${getFileIconClass(file)}`}
                                    >
                                        {renderFileIcon(file)}
                                    </div>
                                    <div className="pd-doc__body">
                                        <button
                                            className="pd-doc__name"
                                            disabled={!file.id || !file.path}
                                            onClick={() => handleOpenAgreementPreview(file)}
                                            title={t("agreements.open")}
                                            type="button"
                                        >
                                            {file.name}
                                        </button>
                                        {fileDate ? (
                                            <div className="pd-doc__meta">{fileDate}</div>
                                        ) : null}
                                    </div>
                                    <IconButton
                                        disabled={!file.id || !file.path}
                                        icon="view"
                                        onClick={() => handleOpenAgreementPreview(file)}
                                        size="sm"
                                        title={t("agreements.open")}
                                        variant="secondary"
                                    />
                                    <IconButton
                                        disabled={isPrinting}
                                        icon="print"
                                        loading={isPrinting}
                                        onClick={() => handlePrintAgreementFile(file)}
                                        size="sm"
                                        title={t("agreements.print")}
                                        variant="secondary"
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Modal
                show={Boolean(previewFile)}
                onHide={handleCloseAgreementPreview}
                centered
                size="xl"
                className="agreement-preview-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{previewFile?.name || t("agreements.defaultTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {previewLoading ? (
                        <div className="agreement-preview-state">{t("agreements.previewLoading")}</div>
                    ) : previewError ? (
                        <div className="agreement-preview-state agreement-preview-state--error">
                            {previewError}
                        </div>
                    ) : !previewUrl ? (
                        <div className="agreement-preview-state">{t("agreements.previewNotLoaded")}</div>
                    ) : isPdfPreview ? (
                        <iframe
                            ref={previewFrameRef}
                            src={previewUrl}
                            title={previewFile?.name || t("agreements.defaultTitle")}
                            className="agreement-preview-frame"
                        />
                    ) : isImagePreview ? (
                        <img
                            src={previewUrl}
                            alt={previewFile?.name || t("agreements.defaultTitle")}
                            className="agreement-preview-image"
                        />
                    ) : (
                        <div className="agreement-preview-state">
                            {t("agreements.previewUnsupported", {
                                format: previewExt
                                    ? previewExt.toUpperCase()
                                    : t("agreements.fileFallback"),
                            })}
                            {previewUrl ? (
                                <div className="mt-3">
                                    <Button
                                        as="a"
                                        download={previewFile?.name || "agreement"}
                                        href={previewUrl}
                                        variant="secondary">
                                        {t("agreements.download")}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="agreement-preview-footer">
                    <Button
                        onClick={handleCloseAgreementPreview}
                        variant="secondary">
                        {t("agreements.close")}
                    </Button>
                    <Button
                        disabled={!canPrint}
                        onClick={handlePrintAgreement}
                        variant="primary">
                        {t("agreements.print")}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={Boolean(alertMessage)} onHide={() => setAlertMessage("")} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("agreements.notification")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{alertMessage}</Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() => setAlertMessage("")}
                        variant="primary">
                        {t("actions.ok", { ns: "common" })}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
